import { AstroPayCore, AstroPayFullCheckout } from '@astropay/payments-lib';
import { ROUTES } from '@routes/paths';
import { AstroPayProService } from '@services/astropayPro';
import { TSupportProfileDetails } from '@services/astropayPro/types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { APButton } from '@components/atoms/APButton';
import { Spinner } from '@components/atoms/Spinner';
import { Typography } from '@components/atoms/Typography';
import { ProAmountButton } from '@components/molecules/ProAmountButton';
import { EActionType } from '@components/molecules/ProAmountButton/types';
import { MerchantProfile } from '@components/organisms/MerchantProfile';

import { MAX_MESSAGE_LENGTH, PRESETS, TAmountMode } from './types';

export const SupportPro = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { usertag } = useParams();
  const [amountMode, setAmountMode] = useState<TAmountMode>('preset');
  const [selectedPreset, setSelectedPreset] = useState<(typeof PRESETS)[number]>(PRESETS[0]);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [isPaymentsLibRendered, setIsPaymentsLibRendered] = useState<boolean>(false);
  const [storeDetails, setStoreDetails] = useState<TSupportProfileDetails>();
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);

  const amountNumber = amountMode === 'preset' ? selectedPreset : Number(customAmount);
  const isValidAmount = Number.isFinite(amountNumber) && amountNumber > 0;

  const paymentContainerRef = useRef<HTMLDivElement>(null);
  const paymentComponentRef = useRef<AstroPayFullCheckout | null>(null);

  // payments Library cleanup
  useEffect(() => {
    return () => {
      try {
        paymentComponentRef.current?.destroy?.();
        paymentComponentRef.current = null;
        AstroPayCore.destroy();
      } catch (error) {
        console.error('error unmounting payment library', error);
      }
    };
  }, []);

  // store details
  useEffect(() => {
    const getStoreDetails = async () => {
      if (!usertag) {
        navigate(ROUTES.GENERIC_ERROR);
        return;
      }
      try {
        const storeDetailsResponse = await AstroPayProService.getStoreDetails(usertag);
        setStoreDetails(storeDetailsResponse);
      } catch (error) {
        navigate(ROUTES.GENERIC_ERROR);
      } finally {
        setIsLoading(false);
      }
    };
    getStoreDetails();
  }, [usertag]);

  // quick amount action
  const onSelectQuickAmount = (value: (typeof PRESETS)[number]) => {
    AstroPayCore.destroy();
    setIsPaymentsLibRendered(false);
    setIsPaymentCompleted(false);
    setAmountMode('preset');
    setSelectedPreset(value);
  };

  // custom amount action
  const onSelectCustomAmount = () => {
    AstroPayCore.destroy();
    setIsPaymentsLibRendered(false);
    setIsPaymentCompleted(false);
    setAmountMode('custom');
  };

  // cancel or start over action
  const onResetPayment = () => {
    AstroPayCore.destroy();
    setIsPaymentsLibRendered(false);
    setAmountMode('preset');
    setSelectedPreset(PRESETS[0]);
    setIsPaymentCompleted(false);
    setCustomAmount('');
    setMessage('');
  };

  // support action
  const onSendSupport = async () => {
    if (!storeDetails) {
      navigate(ROUTES.GENERIC_ERROR);
      return;
    }
    try {
      // Init payments Library
      AstroPayCore.init(storeDetails?.appId || '', {
        environment: import.meta.env.VITE_ENV,
        styles: { borderRadiusWrapper: 20, width: '100%', paddingWrapper: 0 },
        theme: 'light',
        tracking: false,
      });

      if (paymentContainerRef.current) {
        paymentContainerRef.current.innerHTML = '';

        const astroPayFullCheckoutData = {
          amount: amountNumber,
          currency: storeDetails.currency || 'USD',
          country: storeDetails.country || 'AR',
          description: message || undefined,
          showTitle: false,
          showDescription: false,
          onSuccess: () => setIsPaymentCompleted(true),
        };
        const paymentComponent = new AstroPayFullCheckout(astroPayFullCheckoutData);

        paymentComponent.render(paymentContainerRef.current);
        paymentComponentRef.current = paymentComponent;
      }
      setIsPaymentsLibRendered(true);
    } catch (error) {
      navigate(ROUTES.GENERIC_ERROR);
    }
  };

  if (isLoading) {
    return (
      <div className="m-20 text-center">
        <Spinner />
        <Typography
          text={t('CommonMessages.loading')}
          weight={400}
          className="text-[16px] text-[#64748B] mt-2"
        />
      </div>
    );
  }

  if (storeDetails) {
    return (
      <div className="flex flex-col w-full max-w-2xl mx-auto mb-10">
        <MerchantProfile
          userName={storeDetails?.userName}
          userTag={usertag || storeDetails.userTag}
          isVerified={storeDetails?.verified}
          profilePicture={storeDetails.profilePicture}
        />
        <div className="flex flex-col w-full max-w-lg mx-auto bg-white rounded-2xl p-6 mt-6">
          <div className="mb-4 mt-3">
            <Typography text={t('AstroPayPro.sendSupport')} weight={500} className="text-[22px]" />
          </div>
          <div>
            <div>
              <Typography
                text={t('AstroPayPro.selectAmount')}
                weight={400}
                className="text-[16px] text-[#64748B]"
              />
            </div>
            <div className="flex gap-2 flex-wrap flex-row">
              {PRESETS.map((value) => (
                <ProAmountButton
                  key={value}
                  value={value}
                  currency={storeDetails?.currency || 'ARS'}
                  isSelected={amountMode === 'preset' && selectedPreset === value}
                  actionType={EActionType.PRESET}
                  onClick={() => onSelectQuickAmount(value)}
                />
              ))}
              <ProAmountButton
                isSelected={amountMode === 'custom'}
                onClick={onSelectCustomAmount}
                actionType={EActionType.CUSTOM}
              />
            </div>
          </div>

          <div className="mt-2">
            <div
              className={[
                'grid transition-[grid-template-rows] duration-300 ease-out',
                amountMode === 'custom' ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
              ].join(' ')}
            >
              <div className="overflow-hidden">
                <Typography
                  text={t('AstroPayPro.otherAmount')}
                  weight={400}
                  className="text-[16px] text-[#64748B]"
                />
                <div className="pt-2">
                  <input
                    id="customAmount"
                    className="border rounded-lg w-full h-[35px] px-4"
                    type="number"
                    min={0}
                    step="0.01"
                    inputMode="decimal"
                    placeholder="5.00"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      onSelectCustomAmount();
                    }}
                    autoFocus={amountMode === 'custom'}
                  />
                  <Typography
                    text={t('AstroPayPro.otherAmountHelp')}
                    weight={400}
                    className="text-[12px] text-[#b4c5c3] mt-2 text-right"
                  />
                </div>
              </div>
            </div>
          </div>

          {!isPaymentsLibRendered && (
            <>
              <div className="mt-4">
                <div>
                  <Typography
                    text={`${t('AstroPayPro.customMessage')} (${t('AstroPayPro.optional')})`}
                    weight={400}
                    className="text-[16px] text-[#64748B]"
                  />
                </div>
                <div>
                  <textarea
                    id="customMessage"
                    className="border rounded-lg w-full mt-2 h-[80px] p-4"
                    placeholder={t('AstroPayPro.typeYourSupportMessage')}
                    value={message}
                    maxLength={MAX_MESSAGE_LENGTH}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
                <div className="text-right">
                  <Typography
                    text={`${message.length}/${MAX_MESSAGE_LENGTH}`}
                    weight={400}
                    className="text-[16px] text-[#b4c5c3]"
                  />
                </div>
              </div>
              <div>
                <APButton
                  variant="tertiary"
                  size="large"
                  textKey={'AstroPayPro.selectPaymentMethod'}
                  disabled={!isValidAmount || !storeDetails?.appId}
                  icon="arrowRightLine"
                  onClick={onSendSupport}
                />
              </div>
            </>
          )}
          <div ref={paymentContainerRef} id="payment-container" className="w-full" />
          {isPaymentsLibRendered && (
            <div>
              <APButton
                variant="tertiary"
                size="large"
                textKey={
                  isPaymentCompleted ? 'AstroPayPro.supportAgain' : 'CommonMessages.payment.cancel'
                }
                disabled={!isValidAmount || !storeDetails?.appId}
                onClick={onResetPayment}
              />
            </div>
          )}
          <div className="text-center">
            <Typography
              text={t('AstroPayPro.instantPayment')}
              weight={400}
              className="text-[12px] text-[#b4c5c3]"
            />
          </div>
        </div>
      </div>
    );
  }

  return null;
};
